import type { Constructor, Maybe } from '@adornis/base/utilTypes.js';
import type { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { translateFieldNameParaglide } from '@adornis/field-management/translate-field.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { dotJoinedPathToKeyArray } from '@adornis/object/nested-values.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { html } from 'lit';
import { m } from '../../../localization/out/messages.js';

const isEntityConstructor = (
  value: unknown,
): value is Constructor<AdornisEntity> & { fields: Record<string, unknown> } => {
  return typeof value === 'function' && !!value && 'fields' in value;
};

type FieldDefinitionLike = {
  arrayDepth?: () => number;
  type?: () => unknown;
};

type EntityConstructorWithTypedFields = Constructor<AdornisEntity> & {
  fields: Record<string, FieldDefinitionLike | undefined>;
};

const resolveFieldDefinition = (entityClass: EntityConstructorWithTypedFields, path: string[]) => {
  const keyPath = dotJoinedPathToKeyArray(path.join('.'));
  let currentClass: EntityConstructorWithTypedFields = entityClass;
  let currentField: FieldDefinitionLike | undefined;

  for (const key of keyPath) {
    const isArray = (currentField?.arrayDepth?.() ?? 0) > 0;
    if (isArray && typeof key === 'number') continue;
    if (typeof key !== 'string') return undefined;

    currentField = currentClass.fields[key];
    if (!currentField) return undefined;

    const nextType = currentField.type?.();
    if (isEntityConstructor(nextType)) {
      currentClass = nextType as EntityConstructorWithTypedFields;
    }
  }

  return currentField;
};

export const getUnwindablePathsFromSelectionSet = <T extends AdornisEntity>(
  entityClass: Constructor<T> & { fields: Record<string, unknown> },
  selectionSet: BaseQLSelectionSet<T>,
) => {
  const paths = new Set<string>();

  const collect = (
    currentClass: Constructor<AdornisEntity> & { fields: Record<string, unknown> },
    currentSelectionSet: BaseQLSelectionSet<AdornisEntity>,
    currentPath: string[],
  ) => {
    for (const [key, value] of Object.entries(currentSelectionSet)) {
      const nextPath = [...currentPath, key];
      const fieldDef = resolveFieldDefinition(currentClass as EntityConstructorWithTypedFields, [key]);

      if (!fieldDef) continue;
      if ((fieldDef.arrayDepth?.() ?? 0) > 0) {
        paths.add(nextPath.join('.'));
      }

      if (!value || typeof value !== 'object' || Array.isArray(value)) continue;

      const nextType = fieldDef.type?.();
      if (!isEntityConstructor(nextType)) continue;

      collect(nextType, value as BaseQLSelectionSet<AdornisEntity>, nextPath);
    }
  };

  collect(
    entityClass as unknown as Constructor<AdornisEntity> & { fields: Record<string, unknown> },
    selectionSet as unknown as BaseQLSelectionSet<AdornisEntity>,
    [],
  );

  return [...paths].sort();
};

/**
 * Truncates in the middle instead of at the end: the head shrinks, the last segment after `separator`
 * stays fully visible – for deep paths that trailing segment is the only part that identifies the field.
 */
const middleTruncated = (text: string, separator: string, classes: string) => {
  const tailStart = text.lastIndexOf(separator);
  return html`<p class="flex min-w-0 ${classes}">
    <span class="truncate">${tailStart < 0 ? text : text.slice(0, tailStart)}</span>
    ${tailStart < 0 ? null : html`<span class="shrink-0 max-w-[60%] truncate">${text.slice(tailStart)}</span>`}
  </p>`;
};

type UserListExportChoice = { unwindPath: Maybe<string>; fileFormat: 'csv' | 'pdf' };

const UserListUnwindPathSelectionDialogContent = stateful(
  (props: { unwindablePaths: string[]; resolve: (choice: UserListExportChoice) => void; reject: () => void }) => {
    const [selectedPath, setSelectedPath] = useState<Maybe<string>>(null);
    const [searchValue, setSearchValue] = useState('');

    const normalizedSearch = searchValue.trim().toLowerCase();
    const filteredPaths = props.unwindablePaths.filter(path => {
      if (!normalizedSearch) return true;
      const translatedPath = translateFieldNameParaglide(path, m).toLowerCase();
      return path.toLowerCase().includes(normalizedSearch) || translatedPath.includes(normalizedSearch);
    });

    return html`
      <div class="dialog-container">
        ${DialogHeader({ title: 'Unwind-Pfad wählen', close: props.reject, closeLabel: 'Dialog schließen' })}

        <p>
          Optional: Wählen Sie einen Array-Pfad, der für den Export entrollt werden soll. Standardmäßig ist kein Unwind
          aktiv.
        </p>

        ${TextInput({
          value: searchValue,
          onChanged: value => setSearchValue(value ?? ''),
          placeholder: 'Pfade durchsuchen',
          clearable: true,
          prefix: html`<span class="icon-[material-symbols--search-rounded] text-base-content/70"></span>`,
        })}

        <div
          class="martinus-selection-backdrop grid grid-cols-[minmax(0,1fr)_auto_auto] gap-1 max-h-[55vh] overflow-auto"
        >
          <button
            class=${`martinus-selection-card text-left py-2 ${selectedPath === null ? 'border-primary border-2' : ''}`}
            @click=${() => setSelectedPath(null)}
          >
            <p class="font-semibold truncate">Kein Unwind</p>
            <p class="text-base-content/70 text-sm text-right">${selectedPath === null ? 'Ausgewählt' : ''}</p>
            <span
              class=${`icon-[material-symbols--check-circle-rounded] text-lg ${selectedPath === null ? 'text-primary' : 'text-base-content/30'}`}
            ></span>
          </button>

          ${filteredPaths.map(
            path => html`
              <button
                class=${`martinus-selection-card text-left py-2 ${selectedPath === path ? 'border-primary border-2' : ''}`}
                title=${`${translateFieldNameParaglide(path, m)}\n${path}`}
                @click=${() => setSelectedPath(path)}
              >
                ${middleTruncated(translateFieldNameParaglide(path, m), ' > ', 'font-semibold')}
                <p class="text-base-content/70 text-sm text-right">${selectedPath === path ? 'Ausgewählt' : ''}</p>
                <span
                  class=${`icon-[material-symbols--check-circle-rounded] text-lg ${selectedPath === path ? 'text-primary' : 'text-base-content/30'}`}
                ></span>
              </button>
            `,
          )}
          ${
            filteredPaths.length === 0
              ? html`<p class="col-span-full text-sm text-base-content/70 px-2 py-1">Keine Pfade gefunden.</p>`
              : null
          }
        </div>

        <p class="text-sm text-base-content/70">${filteredPaths.length} von ${props.unwindablePaths.length} Pfaden</p>

        <div class="flex justify-end gap-2 mt-4">
          <button
            class="btn btn-outline"
            @click=${() => props.resolve({ unwindPath: selectedPath, fileFormat: 'pdf' })}
          >
            Als PDF exportieren
          </button>
          <button
            class="btn btn-primary"
            @click=${() => props.resolve({ unwindPath: selectedPath, fileFormat: 'csv' })}
          >
            Als CSV exportieren
          </button>
        </div>
      </div>
    `;
  },
);

export const openUserListUnwindPathSelectionDialog = async (unwindablePaths: string[]) => {
  return showDialog<UserListExportChoice>({
    content: (resolve, reject) => UserListUnwindPathSelectionDialogContent({ unwindablePaths, resolve, reject }),
    modalBoxClasses: 'max-w-[50vw]',
  });
};
